import { Dispatch, SetStateAction } from 'react';
import {
  UpsertFormRequest,
  UpsertScreenRequest,
  EntityDetailType,
  EntityTypeCode,
  ActionType,
  EntityDetailConfig,
  FormDto,
} from '@/apiClient';
import { UniqueIdentifier } from '@dnd-kit/core';

export type TOption = {
  value: string;
  label: string;
};

export enum FormType {
  Animal = 1,
  Member = 2,
  MembershipSettings = 3,
}

export type TFieldConfig = {
  id: string;
  formType: FormType;
  type: FieldType;
  label?: string;
  placeholder?: string;
  dropdownOptions?: TOption[];
  required: boolean;
};

export type FieldType = EntityDetailType;

export type OptionType = {
  id?: string;
  category?: string | null;
  disabled?: boolean;
  sortOrder?: number;
  isArchived?: boolean;
  data?: string;
  label: string;
  value: string | number;
};

export type CommonFieldConfigProperty = {
  id?: string;
  fieldType: FieldType;
  formType: FormType;
  label?: string;
  name?: string;
  placeholder?: string;
  defaultValue?: string;
  description?: string;
  value?: any;
};

export type CheckboxFieldConfigProperty = {
  checked?: boolean;
  options?: OptionType[];
};

export type RadioFieldConfigProperty = {
  options?: OptionType[];
  required?: boolean;
};

export type SelectFieldConfigProperty = {
  multiple?: boolean;
};

export enum InputFieldConfigPropertyType {
  Text = 'text',
  Number = 'number',
  Date = 'date',
  Email = 'email',
  File = 'file',
  Range = 'range',
  Password = 'password',
}
export type InputFieldConfigProperty = {
  inputType?: InputFieldConfigPropertyType;
  required?: boolean;
  min?: number | Date;
  max?: number | Date;
  multiline?: boolean;
  allowedFileTypes?: string;
};

export type FieldConfigType = CommonFieldConfigProperty &
  CheckboxFieldConfigProperty &
  RadioFieldConfigProperty &
  InputFieldConfigProperty &
  SelectFieldConfigProperty;

export type FieldTypeOptionMap = Record<FieldType, OptionType>;
export type FormTypeOptionMap = Record<FormType, OptionType>;
export type InputTypeOptionMap = Record<
  InputFieldConfigPropertyType,
  OptionType
>;

export const FORM_TYPE_LABEL: Record<FormType, string> = {
  [FormType.Animal]: 'Animal',
  [FormType.Member]: 'Member',
  [FormType.MembershipSettings]: 'Membership Settings',
};

export const FORM_TYPE_OPTIONS: OptionType[] = [
  { value: FormType.Animal, label: FORM_TYPE_LABEL[FormType.Animal] },
  { value: FormType.Member, label: FORM_TYPE_LABEL[FormType.Member] },
  {
    value: FormType.MembershipSettings,
    label: FORM_TYPE_LABEL[FormType.MembershipSettings],
  },
];

export const FORM_TYPE_OPTIONS_MAP: FormTypeOptionMap =
  FORM_TYPE_OPTIONS.reduce(
    (acc, option) => ({ ...acc, [option.value]: option }),
    {} as FormTypeOptionMap,
  );

export const INPUT_TYPE_OPTIONS: OptionType[] = [
  {
    label: 'Text',
    value: InputFieldConfigPropertyType.Text,
  },
  {
    label: 'Number',
    value: InputFieldConfigPropertyType.Number,
  },
  {
    label: 'Date',
    value: InputFieldConfigPropertyType.Date,
  },
  {
    label: 'Email',
    value: InputFieldConfigPropertyType.Email,
  },
  {
    label: 'File',
    value: InputFieldConfigPropertyType.File,
  },
  {
    label: 'Range',
    value: InputFieldConfigPropertyType.Range,
  },
  {
    label: 'Password',
    value: InputFieldConfigPropertyType.Password,
  },
];

export const INPUT_TYPE_OPTIONS_MAP: InputTypeOptionMap =
  INPUT_TYPE_OPTIONS.reduce(
    (acc, option) => ({ ...acc, [option.value]: option }),
    {} as InputTypeOptionMap,
  );

export type TBuilderField = FieldConfigType & {
  id: string;
};

export type TBuilderFieldColumn = {
  id: string;
  items: IEntityDetail[];
};

export type TFormBuilderContext = {
  rootItem: TBuilderFieldColumn;
  setRootItem: Dispatch<SetStateAction<TBuilderFieldColumn>>;
  formContainers: TSection[];
  setFormContainers: Dispatch<SetStateAction<TSection[]>>;
  formBasicInfo: FormDto;
  setFormBasicInfo: Dispatch<SetStateAction<FormDto>>;
  swapSections: (oldIndex: number, newIndex: number) => void;
};

export const SORTABLE_TARGET_TYPE = {
  CONTAINER: 'container',
  ITEM: 'item',
};

export const FIELD_CONFIG_STORAGE_KEY = 'breedlogic-field-config';

// SECTION FOR ENTITY INTERFACE

type TConfigValidation = {
  required?: boolean;
  min?: number | null;
  max?: number | null;
};

type TEntityDetailConfig = {
  custom?: EntityDetailConfig['custom'];
  defaultValue?: string | null;
  detailDescription?: string;
  detailName?: string;
  entityDetailConfigEntityId?: string;
  entityDetailConfigId?: number;
  entityDetailId?: number;
  entityDetailUnitOfMeasurement?: number;
  entityDetailUnitOfMeasureScale?: number;
  isDefault?: boolean;
  isEnabled?: boolean;
  isDatabaseSearchEnable?: boolean;
  labelText?: string;
  options: { value: string; label: string }[];
  order?: number;
  organisationId?: number;
  placeholder?: string;
  validation?: TConfigValidation;
};

type TEntityDetailCategory = {
  entityDetailCategoryId: number;
  entityDetailCatENTITY_TYPE_OPTIONSegoryName: string;
  entityDetailCategoryDescription: string;
  entityDetailCategoryAssociations: Array<{
    entityDetailCategoryId: number;
    entityDetailId: number;
  }>;
};

export interface IEntityDetail {
  entityDetaTEntityDetailValueilId?: number;
  entityDetailEntityId?: string;
  entityDetailId?: number;
  entityDetailName?: string;
  entityDetailDescription?: string;
  entityDetailValues?: any; // Temporary mark as any
  entityDetailType: EntityDetailType;
  entityTypeCode?: EntityTypeCode;
  entityDetailConfigs: TEntityDetailConfig;
  entityDetailCategories: Array<TEntityDetailCategory>;
}

export type TSortableItemtype = 'container' | 'item';

export type TSortableItem = {
  id: UniqueIdentifier;
};

export type TSection = {
  title: string;
  id: UniqueIdentifier;
  items: Record<string, IEntityDetail[]>;
};

type ActionTypeOptionMap = Record<ActionType, OptionType>;

export const ACTION_TYPE_OPTIONS: OptionType[] = [
  { label: 'Create', value: ActionType.Create },
  { label: 'Edit', value: ActionType.Edit },
  { label: 'Delete', value: ActionType.Delete },
  { label: 'Studbook', value: ActionType.Studbook },
  { label: 'Quick Search', value: ActionType.QuickSearch },
];

export const ACTION_TYPES: ActionType[] = ACTION_TYPE_OPTIONS.map(
  (option) => option.value as ActionType,
);

export const ACTION_TYPE_OPTIONS_MAP: ActionTypeOptionMap =
  ACTION_TYPE_OPTIONS.reduce(
    (acc, option) => ({ ...acc, [option.value]: option }),
    {} as ActionTypeOptionMap,
  );

export type TFormConfig = Omit<UpsertFormRequest, 'actionType'> & {
  actionType: ActionType;
};

export type TCreateScreenPayload = Omit<
  UpsertScreenRequest,
  'type' | 'forms'
> & {
  type: EntityTypeCode;
  forms: Array<TFormConfig>;
};

export const DEFAULT_FORM_INFO: FormDto = {
  actionType: ActionType.Create,
  entityType: EntityTypeCode.Animal,
  formName: '',
};

export const ENTITY_DETAIL_NAME = {
  ADDRESS: 'Address',
  ANIMAL_STATUS: 'Animal Status',
  ANIMAL_NAME: 'Animal Name',
  ANIMAL_IDENT: 'Animal Ident',
  ANIMAL_TYPE: 'Animal Type',
  SIRE: 'Sire',
  DAM: 'Dam',
  BREEDER: 'Breeder',
  OWNER: 'Owner',
  ORIGINAL_OWNER: 'Original Owner',
  BREED_PERCENTS: 'BreedPercents',
  DATE_OF_BIRTH: 'Date of Birth',
  BY_AI: 'By AI',
  BY_ET: 'By ET',
  SEX: 'Sex',
  TATTOO: 'Tattoo',
  DISPOSAL_RECORD: 'Disposal Record',
  ALTERNATE_IDENTS: 'Alternate Idents',
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  EMAIL: 'Email',
  ENTERPRISE_IDENT: 'Enterprise Ident',
  NAME: 'Name',
  PHONE: 'Phone',
  MEMBERSHIP: 'Membership',
  DATE_JOINED: 'Date Joined',
  DATE_RESIGNED: 'Date Resigned',
  ENTERPRISE_STATUS: 'Enterprise Status',
  BIRTH_WEIGHT: 'Birth Weight',
  SEX_CODE: 'Sex Code',
  COLOUR: 'Colour',
} as const;

export const ENTITY_DETAIL_ID = {
  TEMPLATE_DOCUMENT: 10155,
  TEMPLATE_NAME: 10154,
  TEMPLATE_USED_BY: 10153,
};
